import { useQuery } from '@apollo/client';
import { PremiumUpsellCard } from '@focuslee/shared-mini-app';
import { Box, Stack } from '@mui/material';
import { Avatar, Caption, Text } from '@telegram-apps/telegram-ui';
import { type FC } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { GetProfileQuery, GetProfileQueryVariables } from '../client/generated/graphql';
import { getProfileQuery } from '../client/queries/profile';
import { CommonIcon } from '../components/common/CommonIcon';
import { ProfileActionRow } from '../components/profile/ProfileActionRow';
import { ProfilePageSkeleton } from '../components/profile/skeleton/ProfilePageSkeleton';
import { useFeatureFlag } from '../contexts/FeatureFlagContext';
import { floatingTabbarScrollBottomPad } from '../helpers/floatingTabbarLayout';
import { useProfilePicture } from '../hooks/useProfilePicture';
import { intlLocaleForLanguage, type SupportedLanguage } from '../i18n/resolveLanguage';
import { activityPath, languagePath, premiumPath, rewardsPath } from '../navigation/paths';

const formatJoinedSince = (createdAt: string, locale: string) => {
    const date = new Date(createdAt);
    if (Number.isNaN(date.getTime())) return null;
    const parts = new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'long', year: 'numeric' }).formatToParts(date);
    const month = parts.find(part => part.type === 'month')?.value;
    const year = parts.find(part => part.type === 'year')?.value;
    return month && year ? `${month} ${year}` : new Intl.DateTimeFormat(locale, { month: 'long', year: 'numeric' }).format(date);
};

export const ProfilePage: FC = () => {
    const { loading, data } = useQuery<GetProfileQuery, GetProfileQueryVariables>(getProfileQuery);
    const profilePicture = useProfilePicture();
    const navigate = useNavigate();
    const { t, i18n } = useTranslation('common');
    const rewardsEnabled = useFeatureFlag('rewards');
    const isLoading = loading || !data;

    if (isLoading) return <ProfilePageSkeleton />;

    const { user } = data;
    const displayName = [user.name, user.surname].filter(Boolean).join(' ') || user.username;
    const initials = `${user.name?.[0] ?? ''}${user.surname?.[0] ?? ''}`.toUpperCase() || '?';
    const currentLanguage = i18n.language as SupportedLanguage;
    const joinedSince = formatJoinedSince(user.createdAt, intlLocaleForLanguage(currentLanguage));

    return (
        <Stack
            sx={{
                position: 'relative',
                height: '100%',
                overflow: 'hidden',
                bgcolor: 'var(--tg-theme-secondary-bg-color)',
                color: 'var(--tg-theme-text-color)',
            }}
        >
            <Box
                sx={{
                    position: 'absolute',
                    inset: 0,
                    overflowY: 'auto',
                    overflowX: 'hidden',
                    WebkitOverflowScrolling: 'touch',
                    pt: 'calc(var(--tg-viewport-safe-area-inset-top, 0px) + 5px)',
                    px: 2,
                    pb: floatingTabbarScrollBottomPad,
                }}
            >
                <Stack alignItems="center" spacing={0.75} sx={{ pb: 2 }}>
                    <Avatar size={96} acronym={initials} src={profilePicture} />
                    <Text
                        style={{
                            fontSize: 18,
                            fontWeight: 800,
                            lineHeight: 1.2,
                            textAlign: 'center',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            maxWidth: '100%',
                        }}
                    >
                        {displayName}
                    </Text>
                    {user.username && (
                        <Caption style={{ color: 'var(--tg-theme-hint-color)', fontSize: 12, lineHeight: 1.2 }}>@{user.username}</Caption>
                    )}
                    {joinedSince && (
                        <Caption style={{ color: 'var(--tg-theme-hint-color)', fontSize: 12, lineHeight: 1.2 }}>
                            {t('profile.joined', { date: joinedSince })}
                        </Caption>
                    )}
                </Stack>

                <Stack spacing={1}>
                    <PremiumUpsellCard
                        role={user.role}
                        tariff={user.tariff}
                        subscriptionExpiresAt={user.subscriptionExpiresAt}
                        renewsAutomatically={Boolean(
                            user.subscription && !user.subscription.subscriptionCanceledAt && user.tariff !== 'manual',
                        )}
                        locale={intlLocaleForLanguage(currentLanguage)}
                        onClick={() => navigate(premiumPath)}
                        accentColor="var(--tg-theme-button-color)"
                        title={t('profile.premium.title')}
                        activeTitle={t('profile.premium.activeTitle')}
                        pricePeriodLabel={t('profile.premium.pricePeriod')}
                        focusLeeFeaturesText={t('profile.premium.focusFeatures')}
                        healthLeeFeaturesText={t('profile.premium.healthFeatures')}
                        startTrialDescription={t('profile.premium.trialDescription')}
                        subscribeDescription={t('profile.premium.subscribeDescription')}
                        trialActiveDescription={daysLeft => t('profile.premium.trialActive', { daysLeft })}
                        subscriptionActiveDescription={date => t('profile.premium.subscriptionActive', { date })}
                        premiumExpiresDescription={date => t('profile.premium.manualActiveText', { date })}
                        startTrialButtonLabel={t('profile.premium.startTrial')}
                        subscribeButtonLabel={t('profile.premium.subscribe')}
                    />
                    <ProfileActionRow
                        title={t('profile.activity.title')}
                        subtitle={t('profile.activity.subtitle')}
                        icon={<CommonIcon icon="history" type="filled" size={20} color="var(--tg-theme-hint-color)" />}
                        onClick={() => navigate(activityPath)}
                    />
                    {rewardsEnabled && (
                        <ProfileActionRow
                            title={t('profile.rewards.title')}
                            subtitle={t('profile.rewards.subtitle')}
                            value={
                                <Text style={{ color: 'var(--tg-theme-text-color)', fontSize: 14, fontWeight: 700 }}>
                                    {user.collectedCurrency}
                                </Text>
                            }
                            icon={<CommonIcon icon="paid" type="filled" size={20} color="var(--tg-theme-hint-color)" />}
                            onClick={() => navigate(rewardsPath)}
                        />
                    )}
                    <ProfileActionRow
                        title={t('profile.settings.title')}
                        subtitle={t('profile.settings.subtitle')}
                        icon={<CommonIcon icon="settings" type="filled" size={20} color="var(--tg-theme-hint-color)" />}
                        onClick={() => navigate(languagePath)}
                    />
                </Stack>
            </Box>
        </Stack>
    );
};
